import { deserify, NewSectionList } from '@geneo2-web/shared-ui';
import { LiveQuizSession } from '@protos/learning_management/lms.db_pb';
import { Box, Grow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuizContext } from '../../../../app/Context/LiveQuizContextProvider';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { LIVE_QUIZ_WAITING_ROOM } from '../../../../routeHandling/RoutesNomenclature';
import { LiveQuizJoinCard } from './LiveQuizJoinCard';

// Was 20s — noticeably laggy for a "teacher just started it" moment in a
// live classroom; 5s keeps the discovery call cheap while feeling responsive.
const DISCOVERY_POLL_INTERVAL_MS = 5000;

// New, dedicated "Live Sessions" section on the student home page (not folded
// into ActiveHomeworkList) — a teacher-started live quiz is more time
// critical than homework due later, so this renders above it. Renders inline
// via the shared NewSectionList carousel rather than a blocking popup, since
// a live quiz shouldn't interrupt whatever the student is already doing on
// Home, and a student can be enrolled in more than one section (different
// subjects) — ideally at most one is live at a time, but nothing stops two
// independent teachers from both starting one, so this renders every entry
// discovery returns, not just the first. Discovery is polled (no join code
// exists, so this is how a student learns a session is live at all); a real
// Firestore listener here would need a section-scoped auth token independent
// of any specific session, which is a reasonable follow-up rather than part
// of this pass. Once inside an active session, the rest of the LiveQuiz flow
// is fully real-time via Firestore.
export const LiveQuizSection = () => {
  const navigate = useNavigate();
  const { checkForActiveLiveQuiz, joinLiveQuizSession } = useLiveQuizContext();
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const { live_quiz_sessions } = deserify(
    useAppSelector((state) => state.liveQuiz)
  );
  const [visible, setVisible] = useState(false);
  // Keyed by liveQuizSessionId — more than one card can be on screen, each
  // needs its own independent "Joining…" state.
  const [joiningId, setJoiningId] = useState<string | undefined>();

  useEffect(() => {
    const studentId = userInfo?.studentProfileId;
    if (!studentId) return;

    const poll = async () => {
      const sessions = await checkForActiveLiveQuiz(studentId);
      setVisible(sessions.length > 0);
    };
    poll();
    const interval = setInterval(poll, DISCOVERY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.studentProfileId]);

  const handleJoin = async (session: LiveQuizSession) => {
    const studentId = userInfo?.studentProfileId;
    if (!studentId) return;
    const sessionKey = String(session.liveQuizSessionId);
    setJoiningId(sessionKey);
    const joined = await joinLiveQuizSession(studentId, session);
    setJoiningId(undefined);
    if (joined) navigate(LIVE_QUIZ_WAITING_ROOM);
  };

  if (!visible || !live_quiz_sessions.length) return null;

  return (
    <Grow in={visible} timeout={500}>
      <Box sx={{ mb: 2 }}>
        <NewSectionList
          sectionTitle="Live Quiz"
          background="#EDE7FF"
          itemsPerPage={2}
          items={live_quiz_sessions.map((session) => (
            <LiveQuizJoinCard
              key={String(session.liveQuizSessionId)}
              joining={joiningId === String(session.liveQuizSessionId)}
              onJoin={() => handleJoin(session)}
              taskTitle={session.taskTitle}
              teacherName={session.teacherName}
              subject={session.subject}
              className={session.className}
              sectionName={session.sectionName}
              totalQuestions={session.totalQuestions}
            />
          ))}
        />
      </Box>
    </Grow>
  );
};

export default LiveQuizSection;
